import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import styles from "./AddLocation.module.css";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { GroupedSalesLocations } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import LocationsTab from "./LocationsTab/LocationsTab";
import {
  CustomLocationTab,
  MAX_CUSTOM_LOCATION_LENGTH,
} from "./CustomLocationTab/CustomLocationTab";
import { LocationChange } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import {
  AddLocationTab,
  getDefaultLocationTab,
} from "../../services/locationTab";

const FOOD_ARTICLE_CUSTOM_LOCATION_MAX_LENGTH = 6;

interface AddLocationModalProps {
  visible: boolean;
  onClose: () => void;
  onTabChanged: (tabId: AddLocationTab) => void;
  itemNoAndType: ItemNoAndType | null;
  slid: string;
  customLocation: string;
  onConfirmClick: ({ slid, customLocation }: LocationChange) => void;
  isLoadingLocations: boolean;
  filteredLocations: GroupedSalesLocations;
  totalAssignedSalesSpaceQuantity: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showNamingGuidelines?: boolean;
  withCustomLocations?: boolean;
  hideCustomLocationTab?: boolean;
}

export const AddLocationModal = memo(
  ({
    visible,
    onClose,
    onTabChanged,
    itemNoAndType,
    slid,
    customLocation,
    onConfirmClick,
    isLoadingLocations,
    filteredLocations,
    totalAssignedSalesSpaceQuantity,
    searchQuery,
    setSearchQuery,
    showNamingGuidelines = false,
    withCustomLocations = false,
    hideCustomLocationTab = false,
  }: AddLocationModalProps) => {
    const [newSlid, setNewSlid] = useState<string>(slid);
    const [newCustomLocation, setNewCustomLocation] =
      useState<string>(customLocation);

    const defaultLocationTab = useMemo<AddLocationTab>(() => {
      return getDefaultLocationTab(
        slid,
        customLocation,
        withCustomLocations,
        filteredLocations.customLocations
      );
    }, [
      slid,
      customLocation,
      withCustomLocations,
      filteredLocations.customLocations,
    ]);

    useEffect(() => {
      if (!visible) {
        return;
      }
      setNewSlid(slid);
      setNewCustomLocation(customLocation);
      setSearchQuery("");
      onTabChanged(defaultLocationTab);
    }, [
      visible,
      slid,
      customLocation,
      setSearchQuery,
      onTabChanged,
      defaultLocationTab,
    ]);

    const {
      saveGuardedFunction: handleCloseModal,
      enableSaveGuard,
      disableSaveGuard,
    } = useSaveGuardedFunction(onClose);

    const { t } = useTranslation();

    const customLocationError = useMemo(() => {
      if (
        showNamingGuidelines &&
        newCustomLocation.length > FOOD_ARTICLE_CUSTOM_LOCATION_MAX_LENGTH
      ) {
        return t("locationNameTooLong");
      }

      if (newCustomLocation.length > MAX_CUSTOM_LOCATION_LENGTH) {
        return t("maximumCharactersLengthString", {
          count: MAX_CUSTOM_LOCATION_LENGTH,
        });
      }
      return null;
    }, [newCustomLocation, showNamingGuidelines, t]);

    const handleConfirm = useCallback(() => {
      if (customLocationError) {
        return;
      }
      onConfirmClick({
        slid: newSlid,
        customLocation: newCustomLocation,
      });
      disableSaveGuard();
      onClose();
    }, [
      customLocationError,
      onConfirmClick,
      newSlid,
      newCustomLocation,
      disableSaveGuard,
      onClose,
    ]);

    const handleSlidChange = useCallback(
      (nextSlid: string) => {
        setNewSlid(nextSlid);
        setNewCustomLocation("");
        enableSaveGuard();
      },
      [enableSaveGuard]
    );

    const handleCustomLocationChange = useCallback(
      (nextCustomLocation: string) => {
        setNewCustomLocation(nextCustomLocation);
        setNewSlid("");
        enableSaveGuard();
      },
      [enableSaveGuard]
    );

    const handleTabChange = useCallback(
      (tabPanelId: string) => {
        const tab = tabPanelId as AddLocationTab;
        onTabChanged(tab);
      },
      [onTabChanged]
    );

    return (
      <FixaModal
        visible={visible}
        handleCloseBtn={handleCloseModal}
        data-testid="add-location-modal"
      >
        <FixaSheets
          labelledById="task-management-add-location-header"
          header={
            <FixaModalHeader
              id="task-management-add-location-header"
              title={t("location3String")}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                data-testid="add-location-confirm"
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <FixaTabs
              className={styles["root"]!}
              defaultActiveTab={defaultLocationTab}
              tabs={[
                <FixaTab
                  data-testid="add-location-locations-tab"
                  key="locations"
                  tabPanelId="locations"
                  text={t("locationsString")}
                />,
                ...(!hideCustomLocationTab
                  ? [
                      <FixaTab
                        data-testid="add-location-custom-location-tab"
                        key="customLocation"
                        tabPanelId="customLocation"
                        text={t("customLocationString")}
                      />,
                    ]
                  : []),
              ]}
              onTabChanged={handleTabChange}
              tabPanels={[
                <FixaTabPanel
                  data-testid="add-location-locations-tab-panel"
                  key="locations-tab-panel"
                  tabPanelId="locations"
                >
                  <LocationsTab
                    onSlidChange={handleSlidChange}
                    itemNoAndType={itemNoAndType}
                    selectedSlid={newSlid}
                    selectedCustomLocation={newCustomLocation}
                    onCustomLocationSelect={handleCustomLocationChange}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    isLoadingLocations={isLoadingLocations}
                    filteredLocations={filteredLocations}
                    totalAssignedSalesSpaceQuantity={
                      totalAssignedSalesSpaceQuantity
                    }
                    withCustomLocations={withCustomLocations}
                  />
                </FixaTabPanel>,
                <FixaTabPanel
                  data-testid="add-location-custom-location-tab-panel"
                  key="customLocation-tab-panel"
                  tabPanelId="customLocation"
                >
                  <CustomLocationTab
                    customLocation={newCustomLocation}
                    onCustomLocationChange={handleCustomLocationChange}
                    error={customLocationError}
                    showNamingGuidelines={showNamingGuidelines}
                  />
                </FixaTabPanel>,
              ]}
            />
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    );
  }
);
